import React, { useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import { AppSettings } from '../../config/app-settings.js';
import Breadcrumb from '../../components/general/breadcrumb.jsx';

function EvacuationDocumentation() {
  const context = useContext(AppSettings);

  useEffect(() => {
    context.handleSetAppContentClass('');
    context.handleSetAppSidebarTwo(false);
    context.handleSetAppSidebarWide(false);
    context.handleSetAppSidebarNone(true);
    context.handleSetAppTopMenu(true);

    return () => {
      context.handleSetAppSidebarTwo(false);
      context.handleSetAppSidebarNone(false);
      context.handleSetAppSidebarWide(false);
      context.handleSetAppTopMenu(false);
    };
  }, []);

  return (
    <>
      {/* Breadcrumb */}
      <div className="row">
        <div className="col-sm-12">
          <Breadcrumb />
        </div>
      </div>

      {/* Page Header */}
      <div className="row">
        <div className="col-sm-12">
          <h1 className="page-header mb-3">
            <i className="fa fa-route me-2"></i>
            EVACUATION PORTAL DOCUMENTATION
          </h1>
          <p className="text-gray-600 fs-5 mb-4">
            A guide to managing emergency evacuation events and coordinating evacuation orders.
          </p>
        </div>
      </div>

      {/* Module Overview */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading bg-primary">
              <h4 className="panel-title">Module Overview</h4>
            </div>
            <div className="panel-body">
              <div className="row">
                <div className="col-md-8">
                  <h5 className="mb-3 fs-4">Key Features</h5>
                  <ul className="mb-0">
                    <li className="text-black-400 fs-5 mb-2">
                      Interactive evacuation zone mapping with real-time traffic data
                    </li>
                    <li className="text-black-400 fs-5 mb-2">
                      Create and manage evacuation events
                    </li>
                    <li className="text-black-400 fs-5 mb-2">
                      Issue and commit evacuation orders for specific zones
                    </li>
                    <li className="text-black-400 fs-5 mb-2">
                      Calculate evacuation delay times and travel times
                    </li>
                    <li className="text-black-400 fs-5 mb-2">
                      Track event timeline and order history
                    </li>
                    <li className="text-black-400 fs-5 mb-2">
                      Export evacuation order reports and address lists
                    </li>
                  </ul>
                </div>
                <div className="col-md-4">
                  <div className="bg-gray-900 rounded p-4 text-center">
                    <i className="fa fa-route fa-4x text-primary mb-3"></i>
                    <Link
                      to="/evacuation-portal-panels/evacuation-portal/"
                      className="btn btn-primary btn-sm w-100 fs-5"
                    >
                      Open Evacuation Portal <i className="fa fa-arrow-right ms-2"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 1. Evacuation Zone Map */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <span className="badge bg-primary me-2">1</span>
                Evacuation Zone Map
              </h4>
            </div>
            <div className="panel-body">
              {/* 1.1 Overview */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.1 Overview
                </h5>
                <p className="text-black-400 fs-5 ms-4 mb-2">
                  This page displays the Evacuation Zone system in an interactive map. Information regarding any current Active Evacuation will be shown on the top section of the left panel. On the bottom of the left panel, mapping controls are provided to allow for the viewing/hiding of geographic datasets (e.g. muster stations).
                </p>
                <p className="text-black-400 fs-5 ms-4 mb-3">
                  Selecting a Zone on the map will load the information pertaining to that zone, including real-time traffic/travel-time information. This information will be presented on a panel that will appear to the right-side of the map. The description of the information in this panel are as follows:
                </p>
              </div>

              {/* 1.2 Zone Number */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.2 Evacuation Order – Zone Number
                </h5>
                <p className="text-black-400 fs-5 ms-4">
                  The zone number is shown at the top of the panel. This is a unique ID for the particular zone selected.
                </p>
              </div>

              {/* 1.3 Evacuation Order */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.3 Evacuation Order
                </h5>
                <div className="ms-4">
                  <h6 className="text-black fs-5 mb-2">1.3.1 Order ID</h6>
                  <p className="text-black-400 fs-5 ms-3 mb-3">
                    This is a unique and arbitrary ID assigned to this (draft) evacuation order.
                  </p>

                  <h6 className="text-black fs-5 mb-2">1.3.2 Status</h6>
                  <p className="text-black-400 fs-5 ms-3 mb-3">
                    Status of the order, which is initially "Draft".
                  </p>

                  <h6 className="text-black fs-5 mb-2">1.3.3 Priority</h6>
                  <p className="text-black-400 fs-5 ms-3">
                    Currently not used (defaulted to "High").
                  </p>
                </div>
              </div>

              {/* 1.4 Destination */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.4 Destination
                </h5>
                <p className="text-black-400 fs-5 ms-4">
                  The muster station name and address assigned to this zone (i.e. closest by time).
                </p>
              </div>

              {/* 1.5 Zone Data */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.5 Zone Data
                </h5>
                <div className="ms-4">
                  <h6 className="text-black fs-5 mb-2">1.5.1 Est. Total People</h6>
                  <p className="text-black-400 fs-5 ms-3 mb-3">
                    Based on the latest parcel data uploaded to the platform, the estimated number of people residing in this zone.
                  </p>

                  <h6 className="text-black fs-5 mb-2">1.5.2 Est. Total Vehicles</h6>
                  <p className="text-black-400 fs-5 ms-3 mb-3">
                    Based on the latest parcel data uploaded to the platform, the estimated number of vehicles in the zone. This is an estimate of expected vehicles that will be used in an evacuation, not the total vehicles that may be owned and registered in the zone. This total will be used to estimate the traffic leaving the zone.
                  </p>

                  <h6 className="text-black fs-5 mb-2">1.5.3 Est. Total Dwellings</h6>
                  <p className="text-black-400 fs-5 ms-3 mb-3">
                    Based on the latest parcel data uploaded to the platform, the estimated number of dwellings (i.e. residential unit) in the zone. This total is based on the parcel data and therefore may not include secondary suites or rental apartment units.
                  </p>

                  <h6 className="text-black fs-5 mb-2">1.5.4 Total Addresses</h6>
                  <p className="text-black-400 fs-5 ms-3">
                    Based on the latest parcel data uploaded to the platform, the total addresses in the zone. Note that this may include residential, commercial, industrial, institutional etc. parcel addresses.
                  </p>
                </div>
              </div>

              {/* 1.6 Est. Currently in Zone */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.6 Est. Currently in Zone (Slider: 0-100%)
                </h5>
                <p className="text-black-400 fs-5 ms-4">
                  This slider allows for the adjustment of estimated people and vehicles currently in the zone. This allows for the use of information as to the percent of people that may have already evacuation can be used to calculate more accurate values. The slider is set to a default of 100%, and selecting a percent value less than 100% will reduce the total people and vehicles estimated to currently be in the zone. These new totals will be used in the calculation of evacuation logistics.
                </p>
              </div>

              {/* 1.7 Traffic Control */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.7 Traffic Control (Switch)
                </h5>
                <p className="text-black-400 fs-5 ms-4">
                  If there are traffic control measures currently being applied to the evacuation of the zone (e.g. traffic control personnel, evacuation signal timing, use of additional evacuation lanes, etc.) then switching this to "Active" will increase the effective roadway capacity. As a conservative approach, it is best to have this switch "Inactive" unless traffic control measures are in place at most or all major bottle neck locations (typically intersections).
                </p>
              </div>

              {/* 1.8 Effective Lanes */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.8 Effective Lanes
                </h5>
                <p className="text-black-400 fs-5 ms-4">
                  The effective lanes that are available for zone residents to evacuate the zone. The default effective lanes value is unique to each zone. However, this value can be changed (higher or lower), to mimic actual effective roadway capacities, or can be used to demonstrate the effect of changes to roadway capacity exiting this zone for planning purposes.
                </p>
                <p className="text-black-400 fs-5 ms-4">
                  As a useful visualization, the effective single lane queue length of all vehicles estimated to be evacuating this zone is provided. For example, in highly populated zones, the queue length can be over 20 km long.
                </p>
              </div>

              {/* 1.9 Current Route Travel Time & Distance */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.9 Current Route Travel Time & Distance
                </h5>
                <p className="text-black-400 fs-5 ms-4">
                  The travel time and distance to the designated muster station from the centroid (general center) of the zone is provided. This information is based on real-time traffic conditions and the date and time this data was acquired is noted below the figures.
                </p>
              </div>

              {/* 1.10 Delay Times */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.10 Delay Times
                </h5>
                <p className="text-black-400 fs-5 ms-4 mb-2">
                  The delay time or time to exit the zone is shown here, with the estimated median (middle) car and last car exit times presented. The times will be boxed in green with a green checkmark if the delay times are within an acceptable policy time (currently set to 60 minutes). If a delay time exceeds the policy time, then the box will be red with a warning sign.
                </p>
                <p className="text-black-400 fs-5 ms-4">
                  This metric is considered the most significant for evacuation order decision making, as it presents the duration of time residents may be in danger should their zone be considered in an imminent emergency situation.
                </p>
              </div>

              {/* 1.11 Total Travel Times */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.11 Total Travel Times (door-to-door)
                </h5>
                <p className="text-black-400 fs-5 ms-4">
                  The time expected for the median and last cars to travel from the moment of exiting their home (parcel) in their vehicle and the time to reach the designated muster station. As it is unsure if evacuating vehicles will go to their designated muster station, and the fact that muster stations are not equally distanced to each zone, these travel times, while useful, can be considered less important than Delay Times.
                </p>
              </div>

              {/* 1.12 Process Evacuation Order Report Button */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  1.12 Process Evacuation Order Report Button (blue)
                </h5>
                <p className="text-black-400 fs-5 ms-4">
                  Upon pressing this button, the Evacuation Order Report Page will be opened, showing the same information (and more) of the selected zone.
                </p>
              </div>

              {/* Tips & Notes */}
              <div className="alert alert-info" role="alert">
                <p className="mb-2 fs-5">
                  <strong><i className="fa fa-lightbulb me-2"></i>Tip:</strong> Use the layer selector on the top left of the map view area to choose the map background.
                </p>
                <p className="mb-0 fs-5">
                  <strong><i className="fa fa-info-circle me-2"></i>Note:</strong> If there is no Active Evacuation Event, then the platform will ask to create one. An Active Evacuation Event is required to use the platform to obtain zonal information. However, an Active Evacuation Event is not required to review previous Evacuation Events and Evacuation Orders (e.g. for archival review or research purposes).
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 2. Evacuation Order Report */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <span className="badge bg-primary me-2">2</span>
                Evacuation Order Report
              </h4>
            </div>
            <div className="panel-body">
              {/* 2.1 Accessing this page */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  2.1 Accessing this page
                </h5>
                <p className="text-black-400 fs-5 ms-4 mb-2">This page can be accessed two ways:</p>
                <ul className="text-black-400 fs-5 ms-4">
                  <li className="mb-2">Through the Evacuation Zone Map ("Process Evacuation Order Report" blue button)</li>
                  <li>Directly (empty report) and select a zone on top right pull-down selector [Select a zone…]</li>
                </ul>
              </div>

              {/* 2.2 Evacuation Order Dashboard */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  2.2 Evacuation Order Dashboard
                </h5>
                <p className="text-black-400 fs-5 ms-4">
                  The top panel reproduces the information as discussed in sections 1.2 to 1.12.
                </p>
              </div>

              {/* 2.3 Map View */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  2.3 Map View
                </h5>
                <p className="text-black-400 fs-5 ms-4 mb-2">
                  The selected zone will be presented in map form, with the parcels (light blue dots) shown to illustrate the density and number of parcels in the zone. The primary evacuation route is shown as a black dotted line, leading to the designated muster station shown denoted with a green muster station icon.
                </p>
                <p className="text-black-400 fs-5 ms-4">
                  The map is interactive and can be zoomed into, to allow for the user to study the area.
                </p>
              </div>

              {/* 2.4 Address List */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  2.4 Address List
                </h5>
                <p className="text-black-400 fs-5 ms-4 mb-2">
                  This section (when opened by pressing the down arrow on the right) shows the list of parcel addresses contained in the zone. An address is assigned a "1" under the "Dwellings" column if the particular address is considered a residential building, and is estimated to contain people and vehicles.
                </p>
                <p className="text-black-400 fs-5 ms-4 mb-2">
                  The total number of addresses is shown at the top (blue bar) and the estimated total number of dwellings that are considered to contain people and vehicles is also shown (orange bar).
                </p>
                <p className="text-black-400 fs-5 ms-4">
                  The full address list for a given zone can be exported in an Excel spreadsheet and downloaded to be used in the management and communications of the evacuation order for this zone.
                </p>
              </div>

              {/* 2.5 Evacuation Order Commit and Rescind Form */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  2.5 Evacuation Order Commit and Rescind Form
                </h5>
                <div className="ms-4">
                  <h6 className="text-black-400 fs-5 mb-2">Commit Order Form</h6>
                  <p className="text-black-400 fs-5 ms-3 mb-3">
                    To designate the zone as has been assigned an official Evacuation Order, this form can be used to enter the official date and time of the order, as well as the official who approved of the order, and any pertinent comments.
                  </p>

                  <h6 className="text-black fs-5 mb-2">Rescind Order Form</h6>
                  <p className="text-black-400 fs-5 ms-3">
                    When a evacuation order is set to be lifted, this form can be used to note the date and time of the order rescinding, as well as the approving official and any comments.
                  </p>
                </div>
              </div>

              {/* 2.6 Export Section */}
              <div className="mb-4">
                <h5 className="text-black fs-5 mb-3">
                  <i className="fa fa-angle-right text-primary me-2"></i>
                  2.6 Export Section
                </h5>
                <p className="text-black-400 fs-5 ms-4">
                  When pressed, the orange "Export Image of Evac Order" button will produce a image screen capture of the evacuation order report page.
                </p>
                <div className="alert alert-warning ms-4 mt-2 fs-5" role="alert">
                  <i className="fa fa-exclamation-triangle me-2"></i>
                  <strong>Note:</strong> This function is still under construction.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 3. Evacuation Events Table */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <span className="badge bg-primary me-2">3</span>
                Evacuation Events Table
              </h4>
            </div>
            <div className="panel-body">
              <p className="text-black fs-5">
                This table shows records of each Evacuation Event, including start and end (if applicable) times, total zones evacuated, total people, vehicles, dwellings involved, and status of the Event.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 4. Evacuation Orders Table */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <span className="badge bg-primary me-2">4</span>
                Evacuation Orders Table
              </h4>
            </div>
            <div className="panel-body">
              <p className="text-black fs-5">
                Each of the Evacuation Order that was committed to is shown as a record in this table. Details of each Order are shown, such as associated event, designated muster station, total people, vehicles, and dwellings estimated to be in the zone at the time of evacuation, and commitment and rescinded (if applicable) date-times.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 5. Evacuation Dashboard */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <span className="badge bg-primary me-2">5</span>
                Evacuation Dashboard
              </h4>
            </div>
            <div className="panel-body">
              <p className="text-black fs-5">
                Summary of current live event (for executive and council audiences).
              </p>
              <div className="alert alert-warning fs-5" role="alert">
                <i className="fa fa-clock me-2"></i>
                <strong>Status:</strong> To be determined (TBD)
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 6. Evacuation Settings */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <span className="badge bg-primary me-2">6</span>
                Evacuation Settings
              </h4>
            </div>
            <div className="panel-body">
              <p className="text-black fs-5">
                Default settings and values.
              </p>
              <div className="alert alert-warning fs-5" role="alert">
                <i className="fa fa-clock me-2"></i>
                <strong>Status:</strong> To be determined (TBD)
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 7. Resources and Links */}
      <div className="row mb-4">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">
                <span className="badge bg-primary me-2">7</span>
                Resources and Links
              </h4>
            </div>
            <div className="panel-body">
              <p className="text-black fs-5">
                User definable list.
              </p>
              <div className="alert alert-warning fs-5" role="alert">
                <i className="fa fa-clock me-2"></i>
                <strong>Status:</strong> To be determined (TBD)
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Support Section */}
      <div className="row">
        <div className="col-xl-12">
          <div className="panel panel-inverse">
            <div className="panel-heading bg-gray-900">
              <h4 className="panel-title">
                <i className="fa fa-life-ring me-2"></i>
                Need Help?
              </h4>
            </div>
            <div className="panel-body">
              <div className="row text-center">
                <div className="col-md-12 mb-3">
                  <i className="fa fa-envelope fa-2x text-primary mb-2"></i>
                  <h5 className="text-black fs-5 mb-2">Contact Support</h5>
                  <p className="text-black fs-5 fs-13px mb-2">
                    Email us for technical assistance
                  </p>
                  <a href="mailto:support@acuere.ca?subject=Evacuation Portal Support Request:">support@acuere.ca</a>
                </div>
                {/* <div className="col-md-6 mb-3">
                  <i className="fa fa-book fa-2x text-primary mb-2"></i>
                  <h5 className="text-black fs-5 mb-2">All Documentation</h5>
                  <p className="text-black fs-5 fs-13px mb-2">
                    Browse documentation for other modules
                  </p>
                  <Link to="/documentation" className="text-primary">
                    View All Documentation
                  </Link>
                </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EvacuationDocumentation;